import * as React from 'react';
import { Schema, SchemaField } from '../../../types/Metadata';
import { Group } from 'rcd-cpp-ui/ui/form/Group';
import { Pencil, Revert, SixDots } from '../../Icon/icon';
import { useActions } from '../schemaModalActions';
import { cn, dndCardDetails, getSchema } from '../../../utils';
import { WYSIWYGEditor } from '../../WYSIWYGEditor/WYSIWYGEditor';
import { Icon } from 'rcd-cpp-ui/ui/icon/Icon';
import TooltipPopover from '../../Popover/popover';
import { useSelector } from '../../../utils/useSelector';
import {
  getRevertTooltipContent,
  GroupLabel,
  ItemFieldCard
} from './itemFieldCard';
import { NodeType } from '../../../enums/NodeType';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { PermissionField } from '../FieldCard/permissionField';

interface Props {
  field: SchemaField;
  schemaForm?: Schema;
  index: number;
  treeFields: SchemaField[];
  onMove?: (index1: number, index2: number) => void;
  isDisabled: boolean;
  children?: React.ReactNode;
}

export const ItemFieldParentCard = (p: Props) => {
  const { onMove, index, treeFields, isDisabled } = p;
  const { field, description, isSystemField, isInherited, treeGroupName } =
    p.field;
  const schema = useSelector(s =>
    getSchema(s.planning?.selectedWorkspace?.schemas, NodeType.Item)
  );
  const adminWorkspaceRefId = useSelector(
    s =>
      s.planning?.workspaces?.find(w =>
        w?.labels?.includes(NodeType.AdminWorkspace)
      )?.props?.refId
  );
  const isManagePage = p.schemaForm != null;
  const isConfigurePage = p.schemaForm == null;
  const baseSchemaField = findOne(schema?.baseSchema?.props?.fields, { field });
  const treeGroupType = 'treegroup_' + field;
  const name = treeGroupName;
  const inheritedName = baseSchemaField?.treeGroupName;
  const inheritedDescription = baseSchemaField?.description;
  const baseSchemaIsSystem =
    adminWorkspaceRefId == schema?.baseSchema?.workspaceRefId;
  const { updateField, selectField } = useActions();
  const [isSystemTitleEditable, setIsSystemTitleEditable] =
    React.useState<boolean>(isConfigurePage && inheritedName != name);
  const [isSystemDescriptionEditable, setIsSystemDescriptionEditable] =
    React.useState<boolean>(
      isConfigurePage && inheritedDescription != description
    );
  const selected = useSelector(s => s.schemaModal?.selectedField);
  const isSelected = selected == treeGroupType;
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (isSelected && ref?.current) {
      ref.current.scrollIntoView();
    }
  }, [ref, isSelected]);
  const isEdited =
    (isSystemField || isInherited) &&
    (name != inheritedName ||
      ((!!description || !!inheritedDescription) &&
        description != inheritedDescription));

  const {
    ref: dndRef,
    opacity,
    handlerId
  } = dndCardDetails(onMove, field, index);
  return (
    <div
      className={cn({
        'selected-field': isSelected,
        'disabled-field': isDisabled
      })}
      ref={ref}
    >
      <div
        className="tree-card-container"
        ref={dndRef}
        style={{ opacity }}
        data-handler-id={handlerId}
      >
        <div className="card" onClick={() => selectField(treeGroupType)}>
          <div className="field-col">
            <span>{name || '--'}</span>{' '}
            {isConfigurePage &&
              (!baseSchemaIsSystem ||
                description != inheritedDescription ||
                name != inheritedName) && (
                <span className="edited">{isEdited && '(edited)'}</span>
              )}
          </div>
          {isSelected && (
            <div className="field-form ">
              <Group
                className="name"
                label="Grouped Field Name"
                componentRight={
                  isConfigurePage && (isSystemField || isInherited) ? (
                    <div className="icon-bar">
                      {!isSystemTitleEditable && (
                        <TooltipPopover
                          type={'info'}
                          content={'Override'}
                          packed={true}
                        >
                          <Icon
                            onClick={() => {
                              setIsSystemTitleEditable(true);
                            }}
                          >
                            <Pencil />
                          </Icon>
                        </TooltipPopover>
                      )}
                      {isSystemTitleEditable && <div></div>}

                      {name != inheritedName && (
                        <TooltipPopover
                          type={'info'}
                          content={`Revert to original name: ${inheritedName}`}
                          packed={true}
                        >
                          <Icon
                            onClick={() => {
                              updateField(field, {
                                treeGroupName: inheritedName
                              });
                              setIsSystemTitleEditable(false);
                            }}
                          >
                            <Revert />
                          </Icon>
                        </TooltipPopover>
                      )}
                    </div>
                  ) : null
                }
              >
                <input
                  type="text"
                  className="form-control"
                  disabled={
                    isManagePage || (isDisabled && !isSystemTitleEditable)
                  }
                  value={name || ''}
                  onChange={e => {
                    updateField(field, { treeGroupName: e.target.value });
                  }}
                />
              </Group>
              <Group
                label={GroupLabel.description}
                className="schema-field-description"
                componentRight={
                  isConfigurePage && (isSystemField || isInherited) ? (
                    <div className="icon-bar">
                      {!isSystemDescriptionEditable && (
                        <TooltipPopover
                          type={'info'}
                          content={'Override'}
                          packed={true}
                        >
                          <Icon
                            onClick={() => {
                              setIsSystemDescriptionEditable(true);
                            }}
                          >
                            <Pencil />
                          </Icon>
                        </TooltipPopover>
                      )}
                      {isSystemDescriptionEditable && <div></div>}

                      {(!!description || !!inheritedDescription) &&
                        description != inheritedDescription && (
                          <TooltipPopover
                            type={'info'}
                            content={
                              <WYSIWYGEditor
                                content={`Revert to original description: ${
                                  inheritedDescription || '--'
                                }`}
                                readonly={true}
                                disabled={true}
                                onChange={null}
                              />
                            }
                            packed={true}
                          >
                            <Icon
                              onClick={() => {
                                updateField(field, {
                                  description: inheritedDescription
                                });
                                setIsSystemDescriptionEditable(false);
                              }}
                            >
                              <Revert />
                            </Icon>
                          </TooltipPopover>
                        )}
                    </div>
                  ) : null
                }
              >
                <WYSIWYGEditor
                  content={
                    description
                      ? description
                      : isDisabled && !isSystemDescriptionEditable
                      ? '--'
                      : ''
                  }
                  disabled={
                    isManagePage || (isDisabled && !isSystemDescriptionEditable)
                  }
                  readonly={
                    isManagePage || (isDisabled && !isSystemDescriptionEditable)
                  }
                  onChange={e => {
                    updateField(field, { description: e });
                  }}
                />
              </Group>
              {isConfigurePage && (
                <PermissionField
                  schema={schema}
                  f={p.field}
                  onChange={editableCriteria =>
                    updateField(field, { editableCriteria })
                  }
                />
              )}
            </div>
          )}

          {isConfigurePage && isSelected && isEdited && !baseSchemaIsSystem && (
            <div>
              <TooltipPopover
                type="info"
                content={getRevertTooltipContent(
                  p.field,
                  baseSchemaField,
                  ['treeGroupName', 'description'],
                  schema
                )}
                packed={true}
              >
                <span
                  className="icon-bar"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    updateField(field, {
                      treeGroupName: inheritedName,
                      description: inheritedDescription
                    });
                    setIsSystemTitleEditable(false);
                    setIsSystemDescriptionEditable(false);
                  }}
                >
                  <Revert />
                </span>
              </TooltipPopover>
            </div>
          )}
          <div className="field-card-dots">{!!p.onMove && <SixDots />}</div>
        </div>
        {treeFields.map(f => (
          <ItemFieldCard
            key={f.field}
            field={f}
            index={index}
            isDisabled={isDisabled}
            schemaForm={p.schemaForm}
            treeFields={treeFields}
          />
        ))}
      </div>
      {p.children ? p.children : <div></div>}
    </div>
  );
};
